import { FormattedMessage } from 'react-intl';
import { getAuctioneerContextMessageIds } from '@/utils/modalContextMessage';
import { getAuctioneerLogoUrl } from '@/utils/urls';
import { getModalContext } from '@/redux/modules/modal';
import { getSeller } from '@/redux/modules/seller';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Modal from '@liveauctioneers/caterwaul-components/lib/Modal/Modal';
import ModalFooter from '@liveauctioneers/caterwaul-components/lib/Modal/ModalFooter';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/Modal/ModalHeader';
import React from 'react';
import SignUpEmail from './SignUpEmail';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useSelector } from 'react-redux';

type Props = {
    bidderExists: boolean;
    cancelSignUp: (event: any) => void;
    error: string;
    openLoginModal: Function;
    setEmail: Function;
    show: boolean;
    submitCreateAccount: Function;
    submitted: boolean;
    whiteLabel: boolean;
};

const AuctioneerContextSignUpModal = (props: Props) => {
    const {
        bidderExists,
        cancelSignUp,
        error = '',
        openLoginModal,
        setEmail,
        show,
        submitCreateAccount,
        submitted = false,
    } = props;

    const { id: sellerId, type: contextType } = useSelector(getModalContext, isEqual);
    const { logoId, name } = useSelector((state) => getSeller(state, sellerId), isEqual);

    const sellerLogoImage = logoId ? (
        <StyledImageWithFallback alt={name || 'Seller'} src={getAuctioneerLogoUrl(logoId, 140)} />
    ) : (
        <StyledImageWithFallback alt={name || 'Seller'} forceFallback />
    );

    const [headerMessageId, blurbMessageId] = getAuctioneerContextMessageIds(contextType);

    const headerContent = (
        <StyledHeader>
            {sellerLogoImage}
            <Text h2>
                <FormattedMessage id={headerMessageId} />
            </Text>
        </StyledHeader>
    );

    const modalHeader = <ModalHeader onClose={cancelSignUp} title={headerContent} />;

    const modalBody = (
        <BodyWrapper>
            <StyledBlurbText secondary>
                <FormattedMessage id={blurbMessageId} values={{ name }} />
            </StyledBlurbText>
            <SignUpEmail
                bidderExists={bidderExists}
                error={error}
                noBodyMargins
                setEmail={setEmail}
                submitCreateAccount={submitCreateAccount}
                submitted={submitted}
            />
        </BodyWrapper>
    );

    const modalFooter = (
        <ModalFooter>
            <StyledFooterText>
                <FormattedMessage
                    id="signUpModal.footer"
                    values={{
                        link: (
                            <Link key="link" onClick={openLoginModal}>
                                <StyledLinkText>
                                    <FormattedMessage id="signUpModal.footerLink" />
                                </StyledLinkText>
                            </Link>
                        ),
                    }}
                />
            </StyledFooterText>
        </ModalFooter>
    );

    return (
        <StyledModal
            body={modalBody}
            contentLabel="SignUpModal"
            footer={modalFooter}
            header={modalHeader}
            show={show}
        />
    );
};

export default AuctioneerContextSignUpModal;

const StyledModal = styled(Modal).attrs(() => ({
    bodyClassName: 'bodyClassName',
}))`
    .bodyClassName {
        padding: 16px 0 20px 0;
        margin: 0 auto;
        width: 100%;
        max-width: 400px;

        @media (max-width: 767px) {
            padding: 15px 20px 40px;
        }
    }
`;

const StyledHeader = styled.div`
    margin-top: -41px;
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
    max-height: 74px;
    max-width: 100%;
    margin-bottom: 25px;
`;

const StyledBlurbText = styled(Text)`
    text-align: center;
    margin-bottom: 36px;
    display: block;
    & + div {
        padding: 0;
    }
`;

const BodyWrapper = styled.div``;

const StyledFooterText = styled(Text)`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.5px;
`;

const StyledLinkText = styled(StyledFooterText)`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    color: ${({ theme }) => theme.colors.blue100};
`;
